// Paket P2 — PromoProvider (60_PROMOTION.md §2–§4).
//
// Kontext für die Hinweis-Entscheidung. Verwaltet:
//   • die letzte Server-Antwort (decision / variant / target_tier)
//   • den aktuellen App-Start-Marker (Kaltstart, 30-min-Hidden)
//   • den lokalen Nachrichten-Bündel-Zähler
//   • den Chat-Sitzungs-Zähler je `chatId`
//   • die Sitzungs-Caps (max. 1 Dialog, max. 2 Banner)
//   • den 90-s-Start-Lock
//   • den Preview-Modus (admin-only, localStorage `lorath_promo_preview`)
//
// Der Provider rendert nichts selbst; die UI lebt in `MembershipPromoHost`.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { getBestandsschutz } from '../api/membership';
import {
  getPromoState, postPromoEvent, ackPromo, markIntroSeen,
  type PromoDecision, type PromoSnapshot, type PromoSurface, type PromoSegment,
} from '../api/promo';
import { useAuth } from '../auth/AuthProvider';
import { useMembership } from '../hooks/useMembership';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  bumpChatSessionCount, bumpBanner, bumpDialog,
  canShowBanner, canShowDialog, clearIntroLock,
  detectAppStart, isIntroLockedThisSession,
  lockIntroThisSession,
  markAppHidden, PROMO_MESSAGE_BUNDLE_THRESHOLD,
  resetChatSessionCount, accumulateMessageBundle,
} from './promoClientRules';
import { queryKeys } from '../api/queryKeys';

export const PROMO_PREVIEW_STORAGE_KEY = 'lorath_promo_preview';

export interface InlinePromo {
  id: string;
  variant: string;
  target_tier: string | null;
  surface: PromoSurface | null;
  titleKey: string;
  textKey: string;
  ctaKey: string;
}

export interface PromoDecisionState {
  kind: PromoDecision;
  variant: string | null;
  target_tier: string | null;
  surface: PromoSurface | null;
  id: string;
  /** Platzhalter fuer die Texte (z. B. character_name → {name}). */
  params: Record<string, string>;
}

export interface PromoContextValue {
  snapshot: PromoSnapshot | null;
  /**
   * S-33 (7.3.3.3): true, sobald der ERSTE Ladeversuch des Snapshots
   * abgeschlossen ist — Erfolg oder Fehler, wie das alte lokale
   * `state.loaded` in MembershipOnboardingHost. Andere Consumer des
   * Snapshots (z. B. BestandsschutzHost) sollen denselben "fertig geladen
   * oder endgültig fehlgeschlagen"-Zustand ablesen können, ohne selbst noch
   * einen Fetch anzustoßen.
   */
  snapshotLoaded: boolean;
  /** Letzte offene Server-Entscheidung (modal/banner); der Host konsumiert sie. */
  decision: PromoDecisionState | null;
  /** Entscheidung als angezeigt/verworfen markieren (Host). */
  consumeDecision(id: string): void;
  /** Mount-Zeitpunkt der App-Sitzung (90-s-Startsperre, §2). */
  sessionStartedAt: number;
  isPreview: boolean;
  /** Eigene-Nachricht senden → Server informieren. */
  recordMessage(surface: PromoSurface, info?: { chatId?: string | number; characterName?: string }): void;
  /** Kontingent erschöpft → Server informieren. */
  recordQuota(kind: 'coins' | 'messages' | 'live'): void;
  /** Inline-Banner für den Chat. */
  useInlinePromo(surface: PromoSurface, chatId?: string | number | null): InlinePromo | null;
  /** Intro-Dialog einmalig zeigen (Aufruf aus MembershipPromoHost). */
  showIntro: () => void;
  ackAction(id: string, action: 'later' | 'open' | 'dismiss_banner' | 'purchased', variant?: string): Promise<void>;
  ackIntroSeen(): Promise<void>;
  /** Navigation-Helper für CTAs. P2-4: exposure_id wird als Query
   *  angehaengt, damit Variant + Kontaktbezug bis zur Paywall erhalten
   *  bleiben. */
  navigateToMembership(target_tier: string | null, variant: string | null, exposureId?: string | null): void;
}

const PromoContext = createContext<PromoContextValue | null>(null);

function readSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readLocalStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function makeId(): string {
  return `promo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function PromoProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const userId = auth.status === 'authed' ? auth.user?.userId : null;
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isAdmin = !!auth.user?.is_admin;
  const [previewSegmentState, setPreviewSegmentState] = useState<PromoSegment | null>(() => {
    if (!isAdmin) return null;
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(PROMO_PREVIEW_STORAGE_KEY);
    const valid: PromoSegment[] = ['free_new', 'free_lapsed', 'plus', 'ultra'];
    return valid.includes(raw as PromoSegment) ? raw as PromoSegment : null;
  });
  // /me kommt asynchron: is_admin ist beim ersten Render oft noch false.
  // Sobald es bekannt ist, die gespeicherte Vorschau nachlesen.
  useEffect(() => {
    if (!isAdmin) { setPreviewSegmentState(null); return; }
    const raw = readLocalStorage()?.getItem(PROMO_PREVIEW_STORAGE_KEY) ?? null;
    const valid: PromoSegment[] = ['free_new', 'free_lapsed', 'plus', 'ultra'];
    setPreviewSegmentState(valid.includes(raw as PromoSegment) ? raw as PromoSegment : null);
  }, [isAdmin]);
  useEffect(() => {
    if (!isAdmin) return undefined;
    const onChange = (ev: Event) => {
      const value = (ev as CustomEvent<string>).detail;
      const valid: PromoSegment[] = ['free_new', 'free_lapsed', 'plus', 'ultra'];
      setPreviewSegmentState(valid.includes(value as PromoSegment) ? (value as PromoSegment) : null);
      queryClient.invalidateQueries({ queryKey: ['promo'] });
    };
    window.addEventListener('lorath:promo-preview-changed', onChange as EventListener);
    return () => window.removeEventListener('lorath:promo-preview-changed', onChange as EventListener);
  }, [isAdmin, queryClient]);
  const previewSegment = previewSegmentState;

  const query = useQuery({
    queryKey: queryKeys.promo.snapshot(),
    queryFn: getPromoState,
    enabled: auth.status === 'authed',
    staleTime: 60 * 1000,
  });
  const snapshot = query.data ?? null;
  // isFetched bleibt (anders als isPending) auch nach einem fehlgeschlagenen
  // Versuch true — genau das Verhalten, das das alte lokale state.loaded in
  // MembershipOnboardingHost hatte (auch im catch-Zweig auf true gesetzt,
  // damit kein Consumer ewig im Lade-Zustand haengt).
  const snapshotLoaded = query.isFetched || query.isError;

  // Caps, Bündel, Sessions
  const sessionStartedAtRef = useRef<number>(typeof window !== 'undefined' ? Date.now() : 0);
  const chatSessionMapRef = useRef<Record<string, number>>({});
  const pendingMessagesRef = useRef<number>(0);
  const lastMessageReportRef = useRef<number>(0);
  const [decision, setDecision] = useState<PromoDecisionState | null>(null);

  // Beim User-Wechsel alles zurücksetzen — offene Bündel werden vorher geflusht,
  // damit Zählerstände nicht verloren gehen, wenn der Nutzer sich neu anmeldet.
  useEffect(() => {
    return () => {
      const pending = pendingMessagesRef.current;
      if (pending > 0) {
        // Best-effort: ohne Preview, direkt an den Server.
        void postPromoEvent({
          event: 'messages',
          count: pending,
        }).catch(() => undefined);
      }
    };
  }, [userId]);
  useEffect(() => {
    chatSessionMapRef.current = {};
    pendingMessagesRef.current = 0;
    setDecision(null);
    queryClient.removeQueries({ queryKey: ['promo'] });
  }, [userId, queryClient]);

  // Beim Routenwechsel Chat-Session-Zähler für den alten ChatId reset.
  useEffect(() => {
    // Wir können hier den alten ChatId nicht direkt kennen; die Komponenten
    // rufen `useInlinePromo` mit dem neuen ChatId und resetten selbst, wenn
    // die Seite wechselt (Cleanup in chat pages).
    return () => undefined;
  }, [location.pathname]);

  const decide = useCallback(async (
    event: 'app_start' | 'messages' | 'context' | 'quota',
    extra?: { surface?: PromoSurface; count?: number; characterName?: string; quotaKind?: 'coins' | 'messages' | 'live' },
  ): Promise<PromoDecisionState | null> => {
    if (auth.status !== 'authed' || !userId) return null;
    try {
      const response = await postPromoEvent({
        event,
        surface: extra?.surface,
        count: extra?.count,
        character_name: extra?.characterName,
        quota_kind: extra?.quotaKind,
        // Preview-Modus (Admin): Server simuliert das Segment, persistiert nichts.
        preview_segment: previewSegment ?? undefined,
      });
      if (!previewSegment) queryClient.invalidateQueries({ queryKey: ['promo'] });
      if (response.decision === 'none') return null;
      const params: Record<string, string> = {};
      const rawName = response.params?.character_name ?? extra?.characterName;
      if (typeof rawName === 'string' && rawName) params.character_name = rawName;
      const next: PromoDecisionState = {
        kind: response.decision,
        variant: response.variant,
        target_tier: response.target_tier,
        surface: response.surface ?? extra?.surface ?? null,
        id: makeId(),
        params,
      };
      // Eine offene Entscheidung wird nicht ueberschrieben — der Host
      // konsumiert sie zuerst (max. 1 Dialog je Sitzung, §2).
      setDecision((current) => current ?? next);
      return next;
    } catch {
      return null;
    }
  }, [auth.status, previewSegment, queryClient, userId]);

  const consumeDecision = useCallback((id: string) => {
    setDecision((current) => (current && current.id === id ? null : current));
  }, []);

  const recordMessage = useCallback((surface: PromoSurface, info?: { chatId?: string | number; characterName?: string }) => {
    const chatId = info?.chatId != null ? String(info.chatId) : null;
    // Lokaler Bündel-Zähler (frühestens alle 5)
    const localBundle = accumulateMessageBundle({
      incoming: 1,
      pending: pendingMessagesRef.current,
      thresholdHint: PROMO_MESSAGE_BUNDLE_THRESHOLD,
    });
    pendingMessagesRef.current = localBundle.pending;

    // Chat-Sitzungszähler (für Kontext-Banner-Schwelle)
    if (chatId) {
      const map = chatSessionMapRef.current;
      const r = bumpChatSessionCount(map, chatId, snapshot?.rules.context_after ?? 12);
      chatSessionMapRef.current = r.next;
      // Erreicht Schwelle? → context-Event melden
      if (r.reached) {
        chatSessionMapRef.current = resetChatSessionCount(map, chatId);
        void decide('context', { surface, characterName: info?.characterName });
      }
    }

    // Lokales Bündel voll → Server-Event melden
    if (localBundle.shouldReport) {
      lastMessageReportRef.current = Date.now();
      void decide('messages', { count: localBundle.count, surface });
    }
  }, [decide, snapshot?.rules.context_after]);

  const recordQuota = useCallback((kind: 'coins' | 'messages' | 'live') => {
    void decide('quota', { quotaKind: kind });
  }, [decide]);

  // App-Start-Erkennung (Kaltstart oder 30-min-Hidden)
  useEffect(() => {
    if (auth.status !== 'authed' || !userId) return undefined;
    const detection = detectAppStart({
      now: Date.now(),
      sessionStorage: readSessionStorage(),
      localStorage: readLocalStorage(),
      userId,
    });
    if (!detection.shouldReport) return undefined;
    // Das Ereignis zaehlt immer; die 90-s-Startsperre verzoegert nur die
    // Anzeige (MembershipPromoHost haelt die Entscheidung so lange zurueck).
    void decide('app_start');
    return undefined;
  }, [auth.status, decide, userId]);

  // visibilitychange: bei "hidden" → markAppHidden (für die 30-min-Schwelle)
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        // Unvollstaendiges Buendel vor dem Verstecken flushen (S6-Fix):
        // ohne pagehide gehen Nachrichten-Bestaende sonst verloren.
        const pending = pendingMessagesRef.current;
        if (pending > 0) {
          pendingMessagesRef.current = 0;
          void postPromoEvent({ event: 'messages', count: pending }).catch(() => undefined);
        }
        markAppHidden({ now: Date.now(), localStorage: readLocalStorage(), userId });
      } else if (document.visibilityState === 'visible') {
        // Re-Check: nach Rückkehr den Provider einen Cycle laufen lassen.
        const detection = detectAppStart({
          now: Date.now(),
          sessionStorage: readSessionStorage(),
          localStorage: readLocalStorage(),
          userId,
        });
        if (detection.shouldReport) void decide('app_start');
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [auth.status, decide, userId]);

  // pagehide: gleicher Flush wie visibilitychange → "hidden"; deckt Mobile/Tab-Wechsel ab.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPageHide = () => {
      const pending = pendingMessagesRef.current;
      if (pending > 0) {
        pendingMessagesRef.current = 0;
        void postPromoEvent({ event: 'messages', count: pending }).catch(() => undefined);
      }
      markAppHidden({ now: Date.now(), localStorage: readLocalStorage(), userId });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [userId]);

  // Kontingent-Uebergaenge (Review): Tagesnachrichten (Plus/Ultra) und Live-
  // Minuten (Ultra) auf 0 → quota-Ereignis; der Server begrenzt auf 1×/Tag bzw.
  // 1×/Woche. Coins meldet der jeweilige Chat (Saldo-Uebergang).
  const membership = useMembership();
  const quotaSeenRef = useRef<{ msgs: number | null; live: number | null }>({ msgs: null, live: null });
  useEffect(() => {
    if (auth.status !== 'authed' || membership.isLoading) return;
    const ent = membership.entitlements;
    const msgsLeft = ent.msgs_free_daily > 0 && !ent.unlimited_standard ? ent.msgs_free_left_today : null;
    const liveLeft = ent.live_minutes_monthly > 0 ? ent.live_minutes_left : null;
    const prev = quotaSeenRef.current;
    if (msgsLeft !== null && msgsLeft <= 0 && (prev.msgs === null || prev.msgs > 0)) void decide('quota', { quotaKind: 'messages' });
    if (liveLeft !== null && liveLeft <= 0 && (prev.live === null || prev.live > 0)) void decide('quota', { quotaKind: 'live' });
    quotaSeenRef.current = { msgs: msgsLeft, live: liveLeft };
  }, [auth.status, decide, membership.entitlements, membership.isLoading]);

  // Intro-Sperre, falls Bestandsschutz pending ist
  const bestandsschutzQuery = useQuery({
    queryKey: ['bestandsschutz'],
    queryFn: getBestandsschutz,
    enabled: auth.status === 'authed',
    staleTime: 60 * 1000,
  });
  useEffect(() => {
    const session = readSessionStorage();
    if (bestandsschutzQuery.data?.state === 'pending') {
      lockIntroThisSession(session);
    } else {
      clearIntroLock(session);
    }
  }, [bestandsschutzQuery.data?.state]);

  const ackAction = useCallback(async (id: string, action: 'later' | 'open' | 'dismiss_banner' | 'purchased', variant?: string) => {
    try {
      await ackPromo(id, action, variant);
      queryClient.invalidateQueries({ queryKey: ['promo'] });
    } catch {
      // still ok — der Server-Cooldown ist nur ein Nice-to-have.
    }
  }, [queryClient]);

  const ackIntroSeen = useCallback(async () => {
    try {
      await markIntroSeen();
      queryClient.invalidateQueries({ queryKey: ['promo'] });
    } catch {
      /* noop */
    }
  }, [queryClient]);

  const showIntro = useCallback(() => {
    // wird vom MembershipPromoHost aufgerufen; tatsächliche Anzeige lebt dort
  }, []);

  const useInlinePromo = useCallback((
    _surface: PromoSurface,
    _chatId?: string | number | null,
  ): InlinePromo | null => {
    // Inline-Promo wird im MembershipPromoHost zentral gerendert und via
    // window-Event in die Chats gespiegelt (PROMO_BANNER_EVENT). Hier nur Stub.
    return null;
  }, []);

  const navigateToMembership = useCallback((target_tier: string | null, variant: string | null, exposureId?: string | null) => {
    const tier = target_tier || 'plus';
    const src = variant || 'promo';
    const params = new URLSearchParams();
    params.set('tier', tier);
    params.set('src', src);
    if (variant) params.set('variant', variant);
    if (exposureId) params.set('exposure_id', exposureId);
    navigate(`/mitgliedschaft?${params.toString()}`);
  }, [navigate]);

  const value: PromoContextValue = useMemo(() => ({
    snapshot,
    snapshotLoaded,
    decision,
    consumeDecision,
    sessionStartedAt: sessionStartedAtRef.current,
    isPreview: !!previewSegment,
    recordMessage,
    recordQuota,
    useInlinePromo,
    showIntro,
    ackAction,
    ackIntroSeen,
    navigateToMembership,
  }), [snapshot, snapshotLoaded, decision, consumeDecision, previewSegment, recordMessage, recordQuota, useInlinePromo, showIntro, ackAction, ackIntroSeen, navigateToMembership]);

  return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
}

export function usePromo(): PromoContextValue {
  const value = useContext(PromoContext);
  if (value) return value;
  // Defensiv: wenn der Provider fehlt (ältere Tests, isolierte Mounts in
  // Storybook etc.), geben wir einen No-Op-Default zurück, damit die Chats
  // nicht hart brechen. In Produktion liegt immer ein PromoProvider oberhalb.
  return {
    snapshot: null,
    snapshotLoaded: false,
    decision: null,
    consumeDecision: () => undefined,
    sessionStartedAt: 0,
    isPreview: false,
    recordMessage: () => undefined,
    recordQuota: () => undefined,
    useInlinePromo: () => null,
    showIntro: () => undefined,
    ackAction: async () => undefined,
    ackIntroSeen: async () => undefined,
    navigateToMembership: (_target: string | null, _variant: string | null, _exposure?: string | null) => undefined,
  };
}

/** Re-exports für die Caps — Tests können sie separat prüfen. */
export { canShowDialog, canShowBanner, bumpDialog, bumpBanner };
